import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import { message } from 'antd'
import * as Sentry from '@sentry/react'
import { config } from '../config/env'
import { generateCorrelationId } from './correlation-id'
import { getErrorMessage } from './error-code-mapping'

// Type definitions
interface RetryAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

interface ExtendedAxiosRequestConfig extends RetryAxiosRequestConfig {
  metadata?: {
    start: number;
  };
  suppressErrorToast?: boolean;
}

interface RefreshResponse {
  tokens: {
    access_token: string;
    refresh_token: string;
  };
}

interface ApiErrorResponse {
  error_code?: string;
  message?: string;
}

// Token Storage Abstraction
export const TokenStorage = Object.freeze({
  getAccessToken: () => localStorage.getItem('admin_access_token'),
  setAccessToken: (token: string) => localStorage.setItem('admin_access_token', token),
  getRefreshToken: () => localStorage.getItem('admin_refresh_token'),
  setRefreshToken: (token: string) => localStorage.setItem('admin_refresh_token', token),
  clear: () => {
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin-auth-storage'); // remove Zustand persist
  }
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error | AxiosError) => void;
}> = [];

const processQueue = (error: Error | AxiosError | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token as string);
    }
  });
  failedQueue = [];
};

let isLoggingOut = false;

const performLogout = () => {
  if (!isLoggingOut) {
    isLoggingOut = true;
    processQueue(new Error("Logged out"));
    TokenStorage.clear();
    
    // Dispatch event for React components to handle navigation natively.
    // If no listener calls event.preventDefault(), fallback to hard redirect.
    const event = new CustomEvent('auth:logout', { cancelable: true });
    const wasHandledByReact = !window.dispatchEvent(event);
    
    if (!wasHandledByReact) {
      window.location.replace('/login');
    }
  }
};

const authClient = axios.create({
  baseURL: config.apiGateway.baseUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const refreshAuthToken = async (refreshToken: string): Promise<string> => {
  const response = await authClient.post<RefreshResponse>('/admin/auth/refresh', {
    refresh_token: refreshToken
  }, {
    headers: {
      Authorization: '' // Prevent inheriting an expired token if interceptors are ever added to authClient
    }
  });

  const tokens = response.data?.tokens;

  if (!tokens?.access_token) {
    throw new Error('Invalid refresh response');
  }

  TokenStorage.setAccessToken(tokens.access_token);
  if (tokens.refresh_token) {
    TokenStorage.setRefreshToken(tokens.refresh_token);
  }

  return tokens.access_token;
};

/**
 * Create configured Axios instance with interceptors
 */
const createApiClient = () => {
  const client = axios.create({
    baseURL: config.apiGateway.baseUrl,
    timeout: config.apiGateway.timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const extendedConfig = config as ExtendedAxiosRequestConfig;
      
      // Track request duration
      extendedConfig.metadata = { start: performance.now() };

      // Add correlation ID if missing (survives retries)
      if (!extendedConfig.headers['X-Correlation-ID']) {
        extendedConfig.headers['X-Correlation-ID'] = generateCorrelationId();
      }

      // Add JWT token if not explicitly overridden by the caller
      const token = TokenStorage.getAccessToken();
      if (token && !extendedConfig.headers.Authorization) {
        extendedConfig.headers.Authorization = `Bearer ${token}`;
      }

      // Add timestamp
      extendedConfig.headers['X-Request-Time'] = new Date().toISOString();

      return extendedConfig;
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      if (import.meta.env.DEV) {
        const start = (response.config as ExtendedAxiosRequestConfig).metadata?.start;
        if (start) {
          const duration = Math.round(performance.now() - start);
          const method = response.config.method?.toUpperCase();
          const url = response.config.url;
          const id = response.config.headers['X-Correlation-ID'];
          
          console.groupCollapsed(`[API Success] ${method} ${url}`);
          console.log("Correlation ID:", id);
          console.log("Duration:", `${duration}ms`);
          console.log("Status:", response.status);
          console.groupEnd();
        }
      }
      return response
    },
    async (error: AxiosError) => {
      if (import.meta.env.DEV) {
        const start = (error.config as ExtendedAxiosRequestConfig)?.metadata?.start;
        if (start) {
          const duration = Math.round(performance.now() - start);
          const method = error.config?.method?.toUpperCase();
          const url = error.config?.url;
          const id = error.config?.headers['X-Correlation-ID'];
          
          console.groupCollapsed(`[API Error] ${method} ${url}`);
          console.log("Correlation ID:", id);
          console.log("Duration:", `${duration}ms`);
          console.log("Status:", error.response?.status || 'Network Error');
          console.groupEnd();
        }
      }

      // Handle cancelled requests
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }

      // Handle offline mode / network errors
      if (!error.response) {
        if (!navigator.onLine) {
          message.open({ key: 'network-offline', type: 'error', content: "You're offline. Please check your connection." });
        } else {
          message.open({ key: 'network-error', type: 'error', content: "Unable to reach server." });
          // Capture client-side network failure / timeout
          Sentry.captureException(new Error(`Admin API Network Failure: ${error.config?.method?.toUpperCase()} ${error.config?.url}`), {
            extra: {
              url: error.config?.url,
              code: error.code,
              correlation_id: error.config?.headers?.['X-Correlation-ID'],
            },
          });
        }
        return Promise.reject(error);
      }

      // Record Sentry Breadcrumb for 5xx responses (prevent duplicate Sentry issues)
      if (error.response.status >= 500) {
        Sentry.addBreadcrumb({
          category: 'api.server_error',
          message: `Server returned ${error.response.status} for ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
          level: 'warning',
          data: {
            status: error.response.status,
            url: error.config?.url,
            correlation_id: error.config?.headers?.['X-Correlation-ID'],
          },
        });
      }

      // Ignore auth endpoints
      const authEndpoints = ['/login', '/refresh', '/logout', '/mfa/verify', '/mfa/setup'];
      const isAuthEndpoint = Array.isArray(authEndpoints) && authEndpoints.some(path => error.config?.url?.includes(path));

      // Handle 401 - Unauthorized (token expired)
      if (error.response?.status === 401 && !isAuthEndpoint) {
        if (isLoggingOut) {
          return Promise.reject(error);
        }

        const originalRequest = error.config as ExtendedAxiosRequestConfig;

        // Prevent infinite retry loops
        if (originalRequest._retry) {
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest._retry = true;
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return client.request(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        const refreshToken = TokenStorage.getRefreshToken();
        
        if (refreshToken) {
          isRefreshing = true;
          originalRequest._retry = true; // Mark request as retried
          try {
            const access_token = await refreshAuthToken(refreshToken);
            
            processQueue(null, access_token);
            
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            return client.request(originalRequest)
          } catch (refreshError: unknown) {
            const err = refreshError instanceof Error ? refreshError : new Error("Refresh failed");
            processQueue(err, null);
            message.open({ key: 'session-expired', type: 'error', content: 'Session expired. Please login again.' });
            performLogout();
            return Promise.reject(err);
          } finally {
            isRefreshing = false;
          }
        } else {
          message.open({ key: 'session-expired', type: 'error', content: 'Session expired. Please login again.' });
          performLogout();
          return Promise.reject(error);
        }
      }

      // Handle backend error codes and messages
      const responseData = error.response?.data as ApiErrorResponse;
      const errorCode = responseData?.error_code;
      const backendMessage = responseData?.message;

      // Suppress 401 errors for logout specifically since we're clearing the session anyway
      if (error.response?.status === 401 && error.config?.url?.includes('/logout')) {
        return Promise.reject(error);
      }

      if (errorCode) {
        if (!(error.config as ExtendedAxiosRequestConfig)?.suppressErrorToast) {
          message.open({ key: `error-${errorCode}`, type: 'error', content: getErrorMessage(errorCode) });
        }
        return Promise.reject(error)
      }

      // Do not show raw backend messages for validation errors (usually 422) as form fields handle them
      if (error.response?.status !== 422 && backendMessage) {
        if (!(error.config as ExtendedAxiosRequestConfig)?.suppressErrorToast) {
          message.open({ key: 'backend-error', type: 'error', content: backendMessage });
        }
        return Promise.reject(error)
      }

      // Prevent duplicate Ant Design messages for standard HTTP errors
      if (error.response?.status === 403) {
        message.open({ key: 'forbidden', type: 'error', content: 'You do not have permission to perform this action.' });
      } else if (error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after']
        message.open({ key: 'rate-limit', type: 'warning', content: `Too many requests. Please wait ${retryAfter || 60} seconds.` });
      } else if (error.response?.status && error.response.status >= 500) {
        message.open({ key: 'server-error', type: 'error', content: 'Server error. Please try again later.' });
      }

      return Promise.reject(error)
    }
  )

  return client
}

export const apiClient = createApiClient()
export { authClient }
